import { api } from '../lib/api';

export interface User {
  id: string;
  email: string;
  name: string;
  profileImageUrl: string | null;
  bio: string | null;
  phone: string | null;
  contactEmail: string | null;
  isAdmin: boolean;
  mustChangePassword: boolean;
  lastSeen: string | null;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface SignupInput {
  email: string;
  password: string;
  name: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface UpdateProfileInput {
  name?: string;
  bio?: string;
  profileImageUrl?: string;
  phone?: string;
  contactEmail?: string;
}

/**
 * Sign up a new user
 */
export const signup = async (input: SignupInput): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/signup', input);
  return response.data;
};

/**
 * Log in an existing user
 */
export const login = async (input: LoginInput): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/login', input);
  return response.data;
};

/**
 * Get current user profile
 */
export const getProfile = async (): Promise<User> => {
  const response = await api.get<User>('/auth/profile');
  return response.data;
};

/**
 * Update current user profile
 */
export const updateProfile = async (input: UpdateProfileInput): Promise<User> => {
  const response = await api.patch<User>('/auth/profile', input);
  return response.data;
};

/**
 * Get user profile by ID
 */
export const getUserById = async (userId: string): Promise<User> => {
  const response = await api.get<User>(`/auth/users/${userId}`);
  return response.data;
};

/**
 * Log out the current user
 */
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

/**
 * Change password
 */
export interface ChangePasswordInput {
  currentPassword?: string;
  newPassword: string;
}

export const changePassword = async (input: ChangePasswordInput): Promise<void> => {
  await api.post('/auth/change-password', input);
};

/**
 * Create user by admin
 */
export interface CreateUserInput {
  email: string;
  name: string;
}

export interface CreateUserResponse {
  user: User;
  temporaryPassword: string;
  message: string;
}

export const createUserByAdmin = async (input: CreateUserInput): Promise<CreateUserResponse> => {
  const response = await api.post<CreateUserResponse>('/admin/users', input);
  return response.data;
};

/**
 * Reset user password (admin only)
 */
export interface ResetPasswordResponse {
  message: string;
  temporaryPassword: string;
}

export const resetUserPassword = async (userId: string): Promise<ResetPasswordResponse> => {
  const response = await api.post<ResetPasswordResponse>('/admin/users/reset-password', { userId });
  return response.data;
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async (): Promise<User[]> => {
  const response = await api.get<User[]>('/admin/users');
  return response.data;
};

/**
 * Toggle admin status for a user (admin only)
 */
export const toggleUserAdmin = async (userId: string, isAdmin: boolean): Promise<User> => {
  const response = await api.patch<User>(`/admin/users/${userId}/admin`, { isAdmin });
  return response.data;
};

/**
 * Update a user's name/email (admin only)
 */
export const updateUserByAdmin = async (userId: string, input: { name?: string; email?: string }): Promise<User> => {
  const response = await api.patch<User>(`/admin/users/${userId}`, input);
  return response.data;
};

/**
 * Delete a user (admin only)
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/admin/users/${userId}`);
};

/**
 * Get global admin notification mute setting
 */
export interface NotificationAdminSettings {
  mutedForNonAdmins: boolean;
}

export const getNotificationAdminSettings = async (): Promise<NotificationAdminSettings> => {
  const response = await api.get<NotificationAdminSettings>('/admin/settings/notifications');
  return response.data;
};

/**
 * Update global admin notification mute setting
 */
export const updateNotificationAdminSettings = async (mutedForNonAdmins: boolean): Promise<NotificationAdminSettings> => {
  const response = await api.patch<NotificationAdminSettings>('/admin/settings/notifications', {
    mutedForNonAdmins,
  });
  return response.data;
};

/**
 * Request a password reset link by email (forgot password)
 */
export const forgotPassword = async (email: string): Promise<void> => {
  await api.post('/auth/forgot-password', { email });
};

/**
 * Reset password using a token from the reset email link
 */
export const resetPasswordWithToken = async (token: string, newPassword: string): Promise<void> => {
  await api.post('/auth/reset-password', { token, newPassword });
};

/**
 * Make current user admin (bootstrap - only works if no admins exist)
 */
export const makeFirstUserAdmin = async (): Promise<{ message: string; user: User }> => {
  const response = await api.post<{ message: string; user: User }>('/auth/setup/make-admin', {});
  return response.data;
};
